import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { BillingStatusBadge, BillingTaxKindLabel } from "../../components/billing/BillingBadges";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { createTax, deleteTax, isApiError, listTaxes, updateTax } from "../../lib/billing-api";
import type { Tax, TaxInput, TaxKind, TaxStatus } from "../../lib/billing-types";
import { formatPercent, fromPercentToBp } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

interface FormState {
  id: string | null;
  name: string;
  code: string;
  kind: TaxKind;
  rate: string;
  rateAgency: string;
  status: TaxStatus;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: "",
  code: "",
  kind: "ad_valorem",
  rate: "",
  rateAgency: "",
  status: "active",
};

function TaxesInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canManage = hasPermission("billing.tax.manage");

  const [items, setItems] = useState<Tax[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await listTaxes();
      setItems(page.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (form === null) {
      return;
    }
    setSaving(true);
    setError(null);
    const input: TaxInput = {
      name: form.name.trim(),
      code: form.code.trim(),
      kind: form.kind,
      rate_bp: fromPercentToBp(form.rate),
      rate_agency: form.rateAgency.trim() === "" ? null : form.rateAgency.trim(),
      status: form.status,
    };
    try {
      if (form.id === null) {
        await createTax(input);
      } else {
        await updateTax(form.id, input);
      }
      setForm(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await deleteTax(id);
      setConfirmId(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="page" aria-labelledby="billing-taxes-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="billing-taxes-title">{t("billing.taxes.title")}</h1>
          <p className="muted">{t("billing.taxes.subtitle")}</p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setForm({ ...EMPTY_FORM })}
          >
            {t("billing.taxes.new")}
          </button>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("billing.taxes.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.taxes.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.tax.name")}</th>
                <th scope="col">{t("billing.tax.code")}</th>
                <th scope="col">{t("billing.tax.kind")}</th>
                <th scope="col">{t("billing.tax.rate")}</th>
                <th scope="col">{t("billing.tax.status")}</th>
                {canManage ? (
                  <th scope="col" className="table__actions-col">
                    <span className="sr-only">{t("billing.common.actions")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((tax) => (
                <tr key={tax.id}>
                  <td>{tax.name}</td>
                  <td>
                    <code>{tax.code}</code>
                  </td>
                  <td>
                    <BillingTaxKindLabel kind={tax.kind} />
                  </td>
                  <td>{formatPercent(tax.rate_bp, locale)}</td>
                  <td>
                    <BillingStatusBadge status={tax.status} />
                  </td>
                  {canManage ? (
                    <td className="table__actions-col">
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() =>
                            setForm({
                              id: tax.id,
                              name: tax.name,
                              code: tax.code,
                              kind: tax.kind as TaxKind,
                              rate: String(tax.rate_bp / 100),
                              rateAgency: tax.rate_agency ?? "",
                              status: tax.status as TaxStatus,
                            })
                          }
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setConfirmId(tax.id)}
                        >
                          {t("billing.common.delete")}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {form !== null ? (
        <Modal
          title={form.id === null ? t("billing.tax.create") : t("billing.tax.edit")}
          onClose={() => setForm(null)}
          busy={saving}
        >
          <form onSubmit={(event) => void onSubmit(event)}>
            <div className="form-grid">
              <label className="field" htmlFor="tax-name">
                <span>{t("billing.tax.name")}</span>
                <input
                  id="tax-name"
                  value={form.name}
                  maxLength={120}
                  required
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="tax-code">
                <span>{t("billing.tax.code")}</span>
                <input
                  id="tax-code"
                  value={form.code}
                  maxLength={30}
                  required
                  onChange={(event) => setForm({ ...form, code: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="tax-kind">
                <span>{t("billing.tax.kind")}</span>
                <select
                  id="tax-kind"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value as TaxKind })}
                >
                  <option value="ad_valorem">{t("billing.taxKind.adValorem")}</option>
                  <option value="fixed">{t("billing.taxKind.fixed")}</option>
                  <option value="withholding">{t("billing.taxKind.withholding")}</option>
                </select>
              </label>
              <label className="field" htmlFor="tax-rate">
                <span>{t("billing.tax.rate")}</span>
                <input
                  id="tax-rate"
                  inputMode="decimal"
                  value={form.rate}
                  onChange={(event) => setForm({ ...form, rate: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="tax-agency">
                <span>{t("billing.tax.rateAgency")}</span>
                <input
                  id="tax-agency"
                  value={form.rateAgency}
                  maxLength={200}
                  onChange={(event) => setForm({ ...form, rateAgency: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="tax-status">
                <span>{t("billing.tax.status")}</span>
                <select
                  id="tax-status"
                  value={form.status}
                  onChange={(event) =>
                    setForm({ ...form, status: event.target.value as TaxStatus })
                  }
                >
                  <option value="active">{t("billing.status.active")}</option>
                  <option value="archived">{t("billing.status.archived")}</option>
                </select>
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? t("common.saving") : t("common.save")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmId !== null ? (
        <Modal
          title={t("billing.tax.deleteConfirm")}
          onClose={() => setConfirmId(null)}
          busy={saving}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={saving}
                onClick={() => void onDelete(confirmId)}
              >
                {t("billing.common.delete")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmId(null)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("billing.tax.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </section>
  );
}

export function TaxesPage(): ReactNode {
  return (
    <RequirePermission permission="billing.tax.read">
      <TaxesInner />
    </RequirePermission>
  );
}
